import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Bed, BedDouble, Bath, Users, Clock, Wifi, Tv, Car, Utensils, 
  ChevronLeft, ChevronRight, Shirt, Sun, ToyBrick, LampDesk 
} from 'lucide-react';

export type BedroomData = {
  id: string;
  name: string;
  images: string[];
  beds: string;
  description: string;
  features: Array<{
    name: string;
    icon: keyof typeof iconMapping;
  }>;
};

const iconMapping = {
  bed: Bed,
  'bed-double': BedDouble,
  bath: Bath,
  users: Users,
  clock: Clock,
  wifi: Wifi,
  tv: Tv,
  car: Car,
  utensils: Utensils,
  shirt: Shirt,       
  sun: Sun,           
  'toy-brick': ToyBrick,
  'lamp-desk': LampDesk
};

type BedroomDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  bedroom: BedroomData | null;
};

const BedroomDialog = ({ isOpen, onOpenChange, bedroom }: BedroomDialogProps) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fullScreenImageIndex, setFullScreenImageIndex] = useState(0);

  if (!bedroom) return null;

  // Couleur de marque Ty Gwennili
  const brandColor = "#919682";

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="p-0 max-w-4xl w-[90vw] max-h-[92vh] lg:max-h-[90vh] bg-white flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle className="text-2xl font-bold text-gray-800">{bedroom.name}</DialogTitle>
            <DialogDescription className="sr-only">Détails de {bedroom.name}</DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[calc(92vh-140px)] lg:h-[calc(90vh-140px)]">
            {/* Layout Desktop */}
            <div className="hidden lg:block">
              <div className="w-full px-6 mb-6">
                <div className="w-full aspect-video relative overflow-hidden rounded-lg group bg-gray-100">
                  <Swiper
                    spaceBetween={0}
                    slidesPerView={1}
                    pagination={{ clickable: true }}
                    navigation={{
                      prevEl: '.swiper-button-prev-bedroom-desktop',
                      nextEl: '.swiper-button-next-bedroom-desktop',
                    }}
                    modules={[Pagination, Navigation]}
                    className="w-full h-full"
                  >
                    {bedroom.images.map((image, index) => (
                      <SwiperSlide key={index} className="w-full h-full">
                        <img
                          src={image}
                          alt={`Photo de ${bedroom.name}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  <button className="swiper-button-prev-bedroom-desktop hidden group-hover:flex absolute top-1/2 -translate-y-1/2 left-4 z-10 p-2 bg-white/70 hover:bg-white rounded-full shadow transition-all duration-200">
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button className="swiper-button-next-bedroom-desktop hidden group-hover:flex absolute top-1/2 -translate-y-1/2 right-4 z-10 p-2 bg-white/70 hover:bg-white rounded-full shadow transition-all duration-200">
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              <div className="px-6 pb-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Colonne Gauche : Description uniquement */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-gray-800">Description</h4>
                      <p className="text-gray-700 leading-relaxed">{bedroom.description}</p>
                    </div>
                  </div>
                  
                  {/* Colonne Droite : Équipements (incluant le lit si listé dans les features) */}
                  <div>
                    <h4 className="text-lg font-semibold mb-4 text-gray-800">Équipements de la chambre</h4>
                    <div className="grid grid-cols-1 gap-3">
                      {bedroom.features.map((feature, index) => {
                        const IconComponent = iconMapping[feature.icon] || Utensils;
                        return (
                          <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl border border-gray-100 transition-colors hover:bg-gray-100">
                            <div className="p-2 bg-white rounded-lg shadow-sm flex-shrink-0">
                               <IconComponent style={{ color: brandColor }} className="h-5 w-5" />
                            </div>
                            <span className="text-gray-700 font-medium">{feature.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Layout Mobile */}
            <div className="lg:hidden">
              <div className="space-y-4">
                <div className="px-4">
                  <div className="w-full h-[45vh] relative rounded-lg overflow-hidden shadow-lg group bg-gray-100">
                    <Swiper
                      spaceBetween={0}
                      slidesPerView={1}
                      pagination={{ clickable: true }}
                      navigation={{
                        prevEl: '.swiper-button-prev-bedroom-mobile',
                        nextEl: '.swiper-button-next-bedroom-mobile',
                      }}
                      modules={[Pagination, Navigation]}
                      className="w-full h-full rounded-lg"
                    >
                      {bedroom.images.map((image, index) => (
                        <SwiperSlide key={index} className="w-full h-full">
                          <img
                            src={image}
                            alt={`Photo mobile ${index}`}
                            className="w-full h-full object-cover cursor-pointer"
                            loading="lazy"
                            decoding="async"
                            onClick={() => {
                              setFullScreenImageIndex(index);
                              setIsFullScreen(true);
                            }}
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    <button className="swiper-button-prev-bedroom-mobile flex absolute top-1/2 -translate-y-1/2 left-2 z-10 p-2 bg-white/70 rounded-full shadow"><ChevronLeft className="h-4 w-4"/></button>
                    <button className="swiper-button-next-bedroom-mobile flex absolute top-1/2 -translate-y-1/2 right-2 z-10 p-2 bg-white/70 rounded-full shadow"><ChevronRight className="h-4 w-4"/></button>
                  </div>
                  <p className="text-center text-xs text-gray-400 mt-2">Appuyez sur une photo pour l'agrandir</p>
                </div>
                
                <div className="px-4 pb-4 space-y-6">
                  {/* Description mobile */}
                  <div>
                    <h4 className="text-lg font-semibold mb-2 text-gray-800">Description</h4>
                    <p className="text-gray-700 leading-relaxed text-sm">{bedroom.description}</p>
                  </div>
                  
                  {/* Équipements mobile */}
                  <div>
                    <h4 className="text-lg font-semibold mb-4 text-gray-800">Équipements</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {bedroom.features.map((feature, index) => {
                        const IconComponent = iconMapping[feature.icon] || Utensils;
                        return (
                          <div key={index} className="flex items-center gap-4 p-2 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="p-2 bg-white rounded-lg shadow-sm flex-shrink-0">
                               <IconComponent style={{ color: brandColor }} className="h-4 w-4" />
                            </div>
                            <span className="text-gray-700 text-sm font-medium">{feature.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Plein écran mobile reste inchangé car très utile pour voir les photos */}
      <Dialog open={isFullScreen} onOpenChange={setIsFullScreen}>
        <DialogContent className="p-0 max-w-full w-screen h-screen bg-black flex items-center justify-center lg:hidden">
          <div className="relative w-full h-full">
            <Swiper
              spaceBetween={0}
              slidesPerView={1}
              initialSlide={fullScreenImageIndex}
              navigation={{
                prevEl: '.swiper-btn-fs-prev',
                nextEl: '.swiper-btn-fs-next',
              }}
              modules={[Navigation]}
              className="w-full h-full"
            >
              {bedroom.images.map((image, index) => (
                <SwiperSlide key={index} className="flex items-center justify-center">
                  <img 
                    src={image} 
                    alt={`Photo plein écran ${index + 1}`}
                    className="max-w-full max-h-full object-contain" 
                    loading="lazy"
                    decoding="async"
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            <button className="swiper-btn-fs-prev absolute top-1/2 left-4 z-10 p-3 bg-white/70 rounded-full"><ChevronLeft className="h-6 w-6"/></button>
            <button className="swiper-btn-fs-next absolute top-1/2 right-4 z-10 p-3 bg-white/70 rounded-full"><ChevronRight className="h-6 w-6"/></button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BedroomDialog;